import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../api/apiClient';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Badge } from '../../components/UI/Badge';
import { Modal } from '../../components/UI/Modal';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Pagination } from '../../components/UI/Pagination';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  Users,
  Search,
  Plus,
  Ban,
  CheckCircle,
  RotateCcw,
  Trash2,
  Edit2,
  KeyRound,
} from 'lucide-react';

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);

  // Impersonate
  const { impersonate } = useAuth();

  // Create / Edit User Modal
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    is_admin: false,
    is_subscribed: false,
    account_type: '0',
    locale: 'es',
  });
  const [userErrors, setUserErrors] = useState({});
  const [userSubmitting, setUserSubmitting] = useState(false);

  // Ban Modal
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [userToBan, setUserToBan] = useState(null);
  const [banReason, setBanReason] = useState('Incumplimiento de términos.');
  const [banSubmitting, setBanSubmitting] = useState(false);

  // Delete User Dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [forceDelete, setForceDelete] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const { success, error: toastError } = useToast();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.users({ tab, search, page });
      if (res && res.data) {
        setUsers(res.data.data || []);
        setPagination({
          currentPage: res.data.current_page,
          lastPage: res.data.last_page,
          total: res.data.total,
          from: res.data.from,
          to: res.data.to,
        });
        if (res.counts) setCounts(res.counts);
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar usuarios.');
    } finally {
      setLoading(false);
    }
  }, [tab, search, page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setPage(1);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setUserFormData({
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      is_admin: false,
      is_subscribed: false,
      account_type: '0',
      locale: 'es',
    });
    setUserErrors({});
    setUserModalOpen(true);
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setUserFormData({
      name: u.name || '',
      email: u.email || '',
      password: '',
      password_confirmation: '',
      is_admin: Boolean(u.is_admin),
      is_subscribed: Boolean(u.is_subscribed),
      account_type: String(u.account_type || '0'),
      locale: u.locale || 'es',
    });
    setUserErrors({});
    setUserModalOpen(true);
  };

  const handleUserFormSubmit = async (e) => {
    e.preventDefault();
    setUserSubmitting(true);
    setUserErrors({});

    try {
      if (editingUser) {
        await adminService.updateUser(editingUser.id, userFormData);
        success('Usuario actualizado correctamente.');
      } else {
        await adminService.createUser(userFormData);
        success('Usuario creado exitosamente.');
      }
      setUserModalOpen(false);
      fetchUsers();
    } catch (err) {
      if (err.errors) {
        setUserErrors(err.errors);
      }
      toastError(err.data?.message || err.message || 'Error al procesar usuario.');
    } finally {
      setUserSubmitting(false);
    }
  };

  const handleBan = async (e) => {
    e.preventDefault();
    if (!userToBan) return;
    setBanSubmitting(true);
    try {
      await adminService.banUser(userToBan.id, banReason);
      success(`Usuario ${userToBan.name} suspendido.`);
      setBanModalOpen(false);
      setUserToBan(null);
      fetchUsers();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al suspender usuario.');
    } finally {
      setBanSubmitting(false);
    }
  };

  const handleUnban = async (u) => {
    try {
      await adminService.unbanUser(u.id);
      success(`Suspensión removida para ${u.name}.`);
      fetchUsers();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al reactivar usuario.');
    }
  };

  const handleRestore = async (u) => {
    try {
      await adminService.restoreUser(u.id);
      success(`Cuenta de ${u.name} restaurada.`);
      fetchUsers();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al restaurar usuario.');
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleteSubmitting(true);
    try {
      await adminService.deleteUser(userToDelete.id, forceDelete);
      success('Operación de eliminación completada.');
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar usuario.');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleImpersonate = async (u) => {
    try {
      await impersonate(u.id);
      success(`Has iniciado sesión como ${u.name}. Redirigiendo a su dashboard...`);
      window.location.href = '/';
    } catch (err) {
      toastError(err.message || 'Error al iniciar impersonación.');
    }
  };

  const tabsList = [
    { key: 'all', label: 'Todos', count: counts.all },
    { key: 'active', label: 'Activos', count: counts.active },
    { key: 'subscribed', label: 'Suscritos (PRO)', count: counts.subscribed },
    { key: 'admins', label: 'Administradores', count: counts.admins },
    { key: 'banned', label: 'Suspendidos', count: counts.banned },
    { key: 'deleted', label: 'Papelera', count: counts.deleted },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Gestión Global de Usuarios
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Administra cuentas, roles, permisos, suspensiones y modo de impersonación
          </p>
        </div>

        <Button onClick={openCreateModal} icon={Plus} className="bg-[#08707A] hover:bg-[#065B63]">
          Crear Usuario
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {tabsList.map((t) => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              tab === t.key
                ? 'bg-[#08707A] text-white shadow-sm font-bold'
                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
          >
            <span>{t.label}</span>
            {t.count !== undefined && (
              <span className={`px-1.5 py-0.2 rounded-md text-[10px] font-extrabold ${tab === t.key ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Users Table Card */}
      <Card>
        <CardHeader>
          <div className="w-full sm:w-80">
            <Input
              placeholder="Buscar por nombre, email o ID PayPal..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando directorio de usuarios..." className="py-12" />
          ) : users.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No se encontraron usuarios"
              description="No hay registros para los filtros seleccionados."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Usuario</th>
                    <th className="py-3 px-4">Rol & Estado</th>
                    <th className="py-3 px-4 text-center">Facturas</th>
                    <th className="py-3 px-4 text-center">Clientes</th>
                    <th className="py-3 px-4 text-center">Inventario</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-xl bg-teal-50 dark:bg-teal-950 text-[#08707A] dark:text-teal-400 font-bold flex items-center justify-center text-xs">
                            {u.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 dark:text-slate-100">{u.name}</p>
                            <p className="text-[11px] text-slate-400">{u.email}</p>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {u.is_admin ? (
                            <Badge variant="admin" size="sm">ADMIN</Badge>
                          ) : (
                            <Badge variant="default" size="sm">Usuario</Badge>
                          )}

                          {u.is_subscribed && (
                            <Badge variant="success" size="sm">PRO</Badge>
                          )}

                          {u.is_banned && (
                            <Badge variant="danger" size="sm">SUSPENDIDO</Badge>
                          )}

                          {u.deleted_at && (
                            <Badge variant="warning" size="sm">PAPELERA</Badge>
                          )}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-center font-bold text-slate-700 dark:text-slate-300">
                        {u.invoices_count || 0}
                      </td>

                      <td className="py-3.5 px-4 text-center font-bold text-slate-700 dark:text-slate-300">
                        {u.customers_count || 0}
                      </td>

                      <td className="py-3.5 px-4 text-center font-bold text-slate-700 dark:text-slate-300">
                        {u.products_count || 0}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Impersonate */}
                          {!u.is_banned && !u.deleted_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={KeyRound}
                              onClick={() => handleImpersonate(u)}
                              title="Iniciar sesión como este usuario (Impersonate)"
                              className="text-[#08707A] hover:bg-teal-50 dark:hover:bg-teal-950/40"
                            />
                          )}

                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Edit2}
                            onClick={() => openEditModal(u)}
                            title="Editar usuario"
                          />

                          {/* Ban / Unban */}
                          {u.is_banned ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={CheckCircle}
                              className="text-emerald-600 hover:bg-emerald-50"
                              onClick={() => handleUnban(u)}
                              title="Reactivar usuario"
                            />
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Ban}
                              className="text-amber-600 hover:bg-amber-50"
                              onClick={() => {
                                setUserToBan(u);
                                setBanModalOpen(true);
                              }}
                              title="Suspender usuario"
                            />
                          )}

                          {/* Restore if in trash */}
                          {u.deleted_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={RotateCcw}
                              className="text-teal-600 hover:bg-teal-50"
                              onClick={() => handleRestore(u)}
                              title="Restaurar de papelera"
                            />
                          )}

                          {/* Delete */}
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setUserToDelete(u);
                              setForceDelete(Boolean(u.deleted_at));
                              setDeleteConfirmOpen(true);
                            }}
                            title={u.deleted_at ? 'Eliminar permanentemente' : 'Mover a papelera'}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      {/* Create / Edit User Modal */}
      <Modal
        isOpen={userModalOpen}
        onClose={() => !userSubmitting && setUserModalOpen(false)}
        title={editingUser ? `Editar Usuario: ${editingUser.name}` : 'Crear Nuevo Usuario'}
        subtitle="Configuración administrativa de cuenta y privilegios"
      >
        <form onSubmit={handleUserFormSubmit} className="p-6 space-y-4">
          <Input
            label="Nombre Completo"
            placeholder="Juan Pérez"
            value={userFormData.name}
            onChange={(e) => setUserFormData(prev => ({ ...prev, name: e.target.value }))}
            error={userErrors.name?.[0]}
            required
            autoFocus
          />

          <Input
            label="Correo Electrónico"
            type="email"
            placeholder="usuario@dominio.com"
            value={userFormData.email}
            onChange={(e) => setUserFormData(prev => ({ ...prev, email: e.target.value }))}
            error={userErrors.email?.[0]}
            required
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={editingUser ? 'Nueva Contraseña (Opcional)' : 'Contraseña'}
              type="password"
              placeholder="••••••••"
              value={userFormData.password}
              onChange={(e) => setUserFormData(prev => ({ ...prev, password: e.target.value }))}
              error={userErrors.password?.[0]}
              required={!editingUser}
            />

            <Input
              label="Confirmar Contraseña"
              type="password"
              placeholder="••••••••"
              value={userFormData.password_confirmation}
              onChange={(e) => setUserFormData(prev => ({ ...prev, password_confirmation: e.target.value }))}
              required={!editingUser || Boolean(userFormData.password)}
            />
          </div>

          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_admin"
                checked={userFormData.is_admin}
                onChange={(e) => setUserFormData(prev => ({ ...prev, is_admin: e.target.checked }))}
                className="w-4 h-4 rounded border-slate-300 text-[#08707A] focus:ring-[#08707A]"
              />
              <label htmlFor="is_admin" className="text-xs font-bold text-slate-800 dark:text-slate-200">
                Rol de Administrador (Acceso al Panel Admin)
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_subscribed"
                checked={userFormData.is_subscribed}
                onChange={(e) => setUserFormData(prev => ({ ...prev, is_subscribed: e.target.checked }))}
                className="w-4 h-4 rounded border-slate-300 text-[#08707A] focus:ring-[#08707A]"
              />
              <label htmlFor="is_subscribed" className="text-xs font-bold text-slate-800 dark:text-slate-200">
                Suscripción Profesional PRO Activa
              </label>
            </div>
          </div>

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button variant="secondary" onClick={() => setUserModalOpen(false)} disabled={userSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={userSubmitting} className="bg-[#08707A] hover:bg-[#065B63]">
              {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Ban User Modal */}
      <Modal
        isOpen={banModalOpen}
        onClose={() => !banSubmitting && setBanModalOpen(false)}
        title={`Suspender Usuario: ${userToBan?.name}`}
        subtitle="Al suspender al usuario, se revocarán todos sus tokens de acceso inmediatamente"
      >
        <form onSubmit={handleBan} className="p-6 space-y-4">
          <Input
            label="Motivo de la Suspensión"
            placeholder="Ej: Incumplimiento de términos o pagos rechazados..."
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            required
            autoFocus
          />

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button variant="secondary" onClick={() => setBanModalOpen(false)} disabled={banSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="danger" isLoading={banSubmitting}>
              Suspender Cuenta
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete User Confirm */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={forceDelete ? '¿Eliminar permanentemente?' : '¿Enviar a papelera?'}
        message={
          forceDelete
            ? `Se eliminará el usuario "${userToDelete?.name}" y TODAS sus facturas, cotizaciones y productos definitivamente de la base de datos.`
            : `El usuario "${userToDelete?.name}" será enviado a la papelera con un plazo de 30 días.`
        }
        confirmText={forceDelete ? 'Eliminar Definitivamente' : 'Enviar a Papelera'}
        isLoading={deleteSubmitting}
      />
    </div>
  );
};
